import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Search, Receipt, MessageCircle, User, ShoppingCart, Bell,
  Store, Plus, Wallet, Heart, LogIn, Gauge, Globe,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useT } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { getDisplayName, getDisplayInitial } from "../lib/displayName";
import { Verified } from "./Verified";

function useUnread(enabled) {
  const [counts, setCounts] = useState({ notifications: 0, messages: 0, total: 0 });
  useEffect(() => {
    if (!enabled) { setCounts({ notifications: 0, messages: 0, total: 0 }); return; }
    let alive = true;
    const load = async () => {
      try {
        const r = await api("/notifications/unread-count");
        if (alive) setCounts({ notifications: r.notifications || 0, messages: r.messages || 0, total: r.total || 0 });
      } catch {}
    };
    load();
    const id = setInterval(load, 25000);
    return () => { alive = false; clearInterval(id); };
  }, [enabled]);
  return counts;
}

function Badge({ n }) {
  if (!n) return null;
  return (
    <span className="absolute -top-1.5 -end-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3D5A] text-white text-[10px] font-black flex items-center justify-center border-2 border-white">
      {n > 99 ? "99+" : n}
    </span>
  );
}

export function AppShell({ children }) {
  const { user, guest, logout } = useAuth();
  const { count: cartCount } = useCart();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const isSeller = user?.role === "seller";
  const isDriver = user?.role === "driver";
  const isAdmin = !!user?.is_admin;
  const signedIn = !!user && !guest;
  const { notifications, messages } = useUnread(signedIn);
  const [menuOpen, setMenuOpen] = useState(false);

  const active = (p) => location.pathname === p;

  const tabs = [
    { to: "/", icon: isSeller ? Store : Home, label: isSeller ? t("my_store") : t("tab_home"), show: true },
    { to: isSeller ? "/add-product" : "/search", icon: isSeller ? Plus : Search, label: isSeller ? t("add") : t("tab_search"), show: !isDriver },
    { to: isDriver ? "/driver-dashboard" : "/orders", icon: isDriver ? Gauge : Receipt, label: isDriver ? t("driver_dashboard") : t("tab_orders"), show: signedIn },
    { to: "/chats", icon: MessageCircle, label: t("tab_chats"), show: signedIn, badge: messages },
    { to: "/profile", icon: User, label: t("tab_profile"), show: true, badge: notifications },
  ].filter((x) => x.show);

  return (
    <div className="min-h-screen bg-[#FAFBFF] flex flex-col">
      {/* Top navbar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E8EAF2]">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
            <div className="w-9 h-9 rounded-xl bg-[#FF3D5A] flex items-center justify-center text-white font-black">ن</div>
            <span className="font-black text-lg text-[#161629] hidden sm:block">{t("app_name")}</span>
          </Link>

          {/* Search (desktop) */}
          {!isSeller && (
            <button
              onClick={() => navigate("/search")}
              data-testid="navbar-search"
              className="hidden md:flex items-center gap-2 flex-1 max-w-md bg-[#F7F8FE] rounded-full px-4 h-10 text-[#9CA3AF] text-sm hover:bg-[#F0F2FA] transition-colors"
            >
              <Search size={16} /> {t("search_placeholder")}
            </button>
          )}

          <div className="flex items-center gap-1 ms-auto">
            {/* Compact search icon (mobile, buyers) */}
            {!isSeller && (
              <button
                data-testid="navbar-search-mobile"
                onClick={() => navigate("/search")}
                className="md:hidden p-2 rounded-xl text-[#6B7280] hover:bg-[#F7F8FE]"
                aria-label="بحث"
              >
                <Search size={20} />
              </button>
            )}
            {/* Desktop nav links */}
            <nav className="hidden lg:flex items-center gap-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.to}
                  to={tab.to}
                  data-testid={`nav-${tab.to.replace(/\//g, "") || "home"}`}
                  className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold transition-colors ${active(tab.to) ? "bg-[#FFE5EA] text-[#FF3D5A]" : "text-[#6B7280] hover:bg-[#F7F8FE]"}`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                  <Badge n={tab.badge} />
                </Link>
              ))}
            </nav>

            {/* language toggle */}
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="p-2 rounded-xl text-[#6B7280] hover:bg-[#F7F8FE] flex items-center gap-1 text-xs font-bold"
            >
              <Globe size={18} /> {lang === "ar" ? "EN" : "ع"}
            </button>

            {!isSeller && signedIn && (
              <button data-testid="navbar-cart" onClick={() => navigate("/cart")} className="relative p-2 rounded-xl text-[#6B7280] hover:bg-[#F7F8FE]">
                <ShoppingCart size={20} />
                <Badge n={cartCount} />
              </button>
            )}

            {signedIn && (
              <button data-testid="navbar-notifications" onClick={() => navigate("/notifications")} className="relative p-2 rounded-xl text-[#6B7280] hover:bg-[#F7F8FE]">
                <Bell size={20} />
                <Badge n={notifications} />
              </button>
            )}

            {signedIn ? (
              <div className="relative">
                <button data-testid="navbar-avatar" onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 ps-2">
                  {user?.picture_base64 || user?.picture ? (
                    <img src={user.picture_base64 || user.picture} alt="" className="w-9 h-9 rounded-xl object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-xl bg-[#7C5CFF] text-white flex items-center justify-center font-black">{getDisplayInitial(user)}</div>
                  )}
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className="absolute end-0 mt-2 w-60 bg-white rounded-2xl brand-shadow-md border border-[#E8EAF2] p-2 z-50 animate-fade-in-up">
                      <div className="px-3 py-2 border-b border-[#F0F2FA] mb-1">
                        <div className="flex items-center gap-1 font-black text-[#161629]">
                          {isAdmin ? t("app_name") : getDisplayName(user)} {user?.verified && <Verified size={14} />}
                        </div>
                        <div className="text-xs text-[#6B7280] truncate">{user?.email}</div>
                      </div>
                      <MenuLink to="/profile" icon={User} label={t("tab_profile")} onClick={() => setMenuOpen(false)} />
                      <MenuLink to="/wallet" icon={Wallet} label={`${t("wallet")} • ${(user?.wallet_balance || 0).toFixed(2)} ${t("currency_lyd")}`} onClick={() => setMenuOpen(false)} />
                      <MenuLink to="/my-favorites" icon={Heart} label={t("my_favorites")} onClick={() => setMenuOpen(false)} />
                      {isAdmin && <MenuLink to="/admin" icon={Gauge} label={t("admin_panel")} onClick={() => setMenuOpen(false)} />}
                      <button
                        data-testid="menu-logout"
                        onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[#FF3D5A] font-bold text-sm hover:bg-[#FFE5EA]"
                      >
                        <LogIn size={18} /> {t("logout")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button data-testid="navbar-login" onClick={() => navigate("/login")} className="flex items-center gap-2 bg-[#FF3D5A] text-white font-bold text-sm px-4 py-2 rounded-xl hover:bg-[#FF2548] transition-colors">
                <LogIn size={16} /> {t("login")}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6 pb-24 lg:pb-6">{children}</main>

      {/* Bottom tab bar (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E8EAF2] flex safe-bottom">
        {tabs.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            data-testid={`bottomtab-${tab.to.replace(/\//g, "") || "home"}`}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 relative ${active(tab.to) ? "text-[#FF3D5A]" : "text-[#6B7280]"}`}
          >
            <div className="relative">
              <tab.icon size={22} />
              <Badge n={tab.badge} />
            </div>
            <span className="text-[10px] font-bold">{tab.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}

function MenuLink({ to, icon: Icon, label, onClick }) {
  return (
    <Link to={to} onClick={onClick} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[#161629] font-bold text-sm hover:bg-[#F7F8FE]">
      <Icon size={18} /> <span className="truncate">{label}</span>
    </Link>
  );
}
